
import { Component, Prop, PropSync, Mixins } from 'vue-property-decorator'
import flatpickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { Input } from '@/mixins'

export interface InputDateTimeOptions {
  defaultDate?: string;
  defaultHour?: number;
  defaultMinute?: number;
  maxDate?: string | Date;
  minDate?: string | Date;
}

@Component({
  components: {
    flatpickr
  }
})

export default class InputDateTime extends Mixins(Input) {
  // :value.sync to model to this
  @PropSync('valueSync', { default: null })
  private value!: string | null;

  @Prop({ default: 'date' })
  private readonly type!: 'date' | 'time' | 'datetime';

  @Prop({ default: () => ({}) })
  private readonly config!: InputDateTimeOptions;

  private isSettingValue = false;

  get localValue (): Date | null {
    if (!this.value) return null

    if (this.type === 'time') {
      let time = this.value

      // If backend accidentally returns ISO
      if (time.includes('T')) {
        time = time.split('T')[1]
      }

      time = time.replace('Z', '')

      const parts = time.split(':')

      const hours = parseInt(parts[0], 10) || 0
      const minutes = parseInt(parts[1], 10) || 0
      const seconds = parseInt(parts[2], 10) || 0

      const date = new Date()
      date.setHours(hours, minutes, seconds, 0)

      return date
    }

    // date or datetime
    const parsed = new Date(this.value)
    return isNaN(parsed.getTime()) ? null : parsed
  }

  set localValue (newDate: Date | null) {
    if (this.isSettingValue) return

    this.isSettingValue = true

    try {
      if (!newDate) {
        if (this.value !== null) {
          this.value = null
        }
        return
      }

      const dateItem = Array.isArray(newDate) ? newDate[0] : newDate
      let date: Date

      if (typeof dateItem === 'string') {
        date = new Date(dateItem)
      } else if (dateItem instanceof Date) {
        date = dateItem
      } else {
        return
      }

      if (this.type === 'time') {
        const parts = dateItem.toString().split(':')

        const hours = parseInt(parts[0], 10) || 0
        const minutes = parseInt(parts[1], 10) || 0
        const seconds = parseInt(parts[2], 10) || 0

        const timeString = `${hours}:${minutes}:${seconds}`

        console.log(timeString)

        if (this.value !== timeString) {
          this.value = timeString
          this.$emit('input', timeString)
        }
        return
      }

      const utcIso = date.toISOString()

      // Only update if the value actually changed
      if (this.value !== utcIso) {
        this.value = utcIso
        this.$emit('input', utcIso)
      }
    } finally {
      // Use nextTick to ensure the update cycle completes
      this.$nextTick(() => {
        this.isSettingValue = false
      })
    }
  }

  get totalConfig (): Record<string, unknown> {
    switch (this.type) {
      case 'date':
        return {
          altInput: true,
          dateFormat: 'Y-m-d', // ISO date format
          ...this.config
        }
      case 'time':
        return {
          altInput: true,
          enableTime: true,
          noCalendar: true,
          dateFormat: 'H:i:S',
          time_24hr: false, // Be explicit about 12/24 hour
          altFormat: 'h:i K',
          ...this.config
        }
      case 'datetime':
        return {
          altInput: true,
          enableTime: true,
          dateFormat: 'Y-m-d H:i:S', // ISO-like format
          altFormat: 'd M y h:i K',
          mode: 'single',
          ...this.config
        }
      default:
        return { altInput: true, ...this.config } // Fallback case
    }
  }
}

